import { useMemo, useState } from 'react';
import {
  POINTAGE_OTA_LABELS,
  parseOtaExport,
  pointage,
  type OtaExport,
  type PmsPointageRow,
  type PointageOta,
  type PointageResult,
  type ReservationView,
} from '@lemontaigu/pms-core';
import { usePms, usePmsScope } from './PmsProvider.js';
import { fmtDate, fmtEuros } from './logic.js';

// « Pointage OTA » (CX2, docs/PMS-CHANNEX-CUTOVER-BRIEF.md F3) — an OTA
// extranet's list of bookings against the PMS's, matched on the OTA's number:
// in both · only on the OTA · only in the PMS. Run before, during and after the
// switch to Channex; « absente du PMS » on a live booking is a lost booking.
//
// One établissement (the page's choice) and one OTA at a time. The extranet's
// list comes as its CSV export (Booking.com « Réservations » → Télécharger;
// Expedia « Réservations » → Exporter). Read-only: nothing here writes.

const STATUS_FR = { ok: 'Confirmée', cancelled: 'Annulée', no_show: 'No-show' } as const;

const toRow = (r: ReservationView): PmsPointageRow => ({
  id: r.id,
  channel: r.channel,
  channelRef: r.channelRef,
  status: r.status,
  noShow: r.noShow,
  checkinDate: r.checkinDate,
  checkoutDate: r.checkoutDate,
  totalCents: r.totalCents,
  guestName: `${(r.guest?.lastName ?? '?').toUpperCase()} ${r.guest?.firstName ?? ''}`.trim(),
});

const pmsStatus = (p: PmsPointageRow) =>
  p.noShow ? 'No-show' : p.status === 'cancelled' ? 'Annulée' : p.status === 'checked_out' ? 'Partie' : 'Active';

export function PointageOta() {
  const pms = usePms();
  const scope = usePmsScope();
  const [ota, setOta] = useState<PointageOta>('booking');
  const [file, setFile] = useState<{ name: string; parsed: OtaExport } | null>(null);
  const [rows, setRows] = useState<PmsPointageRow[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async (f: File) => {
    setErr('');
    setRows(null);
    setFile(null);
    const parsed = parseOtaExport(await f.text());
    if (!parsed.ok) return setErr(parsed.error);
    if (parsed.value.ota) setOta(parsed.value.ota);
    const arrivals = parsed.value.lines.map((l) => l.arrival).filter((d): d is string => d !== null).sort();
    if (arrivals.length === 0) return setErr('Aucune date d’arrivée lisible dans le fichier.');
    setBusy(true);
    const r = await pms.reservations.listArriving(arrivals[0]!, arrivals[arrivals.length - 1]!);
    setBusy(false);
    if (!r.ok) return setErr(r.error.message);
    setFile({ name: f.name, parsed: parsed.value });
    setRows(r.value.map(toRow));
  };

  const result: PointageResult | null = useMemo(
    () => (file && rows ? pointage(file.parsed.lines, rows, ota) : null),
    [file, rows, ota],
  );
  const lost = result?.onlyOta.filter((o) => o.missing).length ?? 0;
  const disagree = result?.both.filter((b) => b.diffs.length > 0).length ?? 0;
  const pmsOnlyLive = result?.onlyPms.filter((o) => o.live).length ?? 0;

  return (
    <div className="pms">
      <div className="pms-head">
        <h2 className="section-title">Pointage OTA</h2>
        <span className="pms-hint">
          L’extranet contre le PMS, par n° de réservation OTA — avant, pendant et après le passage à Channex. Lecture
          seule.
        </span>
      </div>

      <div className="pms-toolbar pms-pointage-bar">
        <label>
          OTA{' '}
          <select value={ota} onChange={(e) => setOta(e.target.value as PointageOta)}>
            {(Object.keys(POINTAGE_OTA_LABELS) as PointageOta[]).map((o) => (
              <option key={o} value={o}>
                {POINTAGE_OTA_LABELS[o]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Export de l’extranet (CSV){' '}
          <input
            type="file"
            accept=".csv,text/csv"
            aria-label="Export de l’extranet"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void load(f);
            }}
          />
        </label>
        {busy && <span className="pms-hint">Lecture du PMS…</span>}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      {result && file && (
        <>
          <p className="pms-hint">
            {file.name} · {file.parsed.lines.length} ligne(s) · {scope.current} · arrivées du{' '}
            {result.window ? `${fmtDate(result.window.from)} au ${fmtDate(result.window.to)}` : '—'} · colonnes lues :{' '}
            {Object.values(file.parsed.columns).join(', ')}
          </p>
          <div className="pms-pointage-sum">
            <span className={lost > 0 ? 'pms-tag is-overdue' : 'pms-tag'}>{lost} absente(s) du PMS</span>
            <span className={disagree > 0 ? 'pms-tag is-vad' : 'pms-tag'}>{disagree} en désaccord</span>
            <span className={pmsOnlyLive > 0 ? 'pms-tag is-vad' : 'pms-tag'}>{pmsOnlyLive} absente(s) du fichier</span>
            <span className="pms-tag">{result.both.length} dans les deux</span>
          </div>

          <h3>Seulement sur l’extranet</h3>
          <table className="pms-table">
            <thead>
              <tr>
                <th>N° OTA</th>
                <th>Client</th>
                <th>Séjour</th>
                <th>Statut OTA</th>
                <th className="pms-num">Montant OTA</th>
              </tr>
            </thead>
            <tbody>
              {result.onlyOta.length === 0 && (
                <tr>
                  <td colSpan={5} className="pms-sub">
                    Aucune.
                  </td>
                </tr>
              )}
              {result.onlyOta.map(({ ota: l, missing }) => (
                <tr key={l.ref} className={missing ? 'pms-pointage-lost' : 'pms-row-cancelled'}>
                  <td>
                    {l.ref}
                    {missing && <span className="pms-tag is-overdue">Absente du PMS</span>}
                  </td>
                  <td>{l.guest ?? '—'}</td>
                  <td>
                    {l.arrival ? fmtDate(l.arrival) : '?'} → {l.departure ? fmtDate(l.departure) : '?'}
                  </td>
                  <td>{l.statusText ?? STATUS_FR[l.status]}</td>
                  <td className="pms-num">{l.amountCents === null ? '—' : fmtEuros(l.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Seulement dans le PMS</h3>
          <table className="pms-table">
            <thead>
              <tr>
                <th>N° OTA</th>
                <th>Client</th>
                <th>Séjour</th>
                <th>Statut PMS</th>
                <th className="pms-num">Total PMS</th>
              </tr>
            </thead>
            <tbody>
              {result.onlyPms.length === 0 && (
                <tr>
                  <td colSpan={5} className="pms-sub">
                    Aucune.
                  </td>
                </tr>
              )}
              {result.onlyPms.map(({ pms: p, live }) => (
                <tr key={p.id} className={live ? undefined : 'pms-row-cancelled'}>
                  <td>
                    {p.channelRef}
                    {live && <span className="pms-tag is-vad">Absente du fichier</span>}
                  </td>
                  <td>{p.guestName}</td>
                  <td>
                    {fmtDate(p.checkinDate)} → {fmtDate(p.checkoutDate)}
                  </td>
                  <td>{pmsStatus(p)}</td>
                  <td className="pms-num">{fmtEuros(p.totalCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Dans les deux</h3>
          <table className="pms-table">
            <thead>
              <tr>
                <th>N° OTA</th>
                <th>Client (PMS)</th>
                <th>Séjour OTA</th>
                <th>Séjour PMS</th>
                <th>Statuts</th>
                <th className="pms-num">OTA / PMS</th>
              </tr>
            </thead>
            <tbody>
              {[...result.both]
                .sort((a, b) => b.diffs.length - a.diffs.length)
                .map(({ ota: l, pms: p, diffs }) => (
                  <tr key={l.ref} className={diffs.length > 0 ? 'pms-pointage-diff' : undefined}>
                    <td>
                      {l.ref}
                      {diffs.includes('dates') && <span className="pms-tag is-vad">Dates</span>}
                      {diffs.includes('status') && <span className="pms-tag is-vad">Statut</span>}
                    </td>
                    <td>{p.guestName}</td>
                    <td>
                      {l.arrival ? fmtDate(l.arrival) : '?'} → {l.departure ? fmtDate(l.departure) : '?'}
                    </td>
                    <td>
                      {fmtDate(p.checkinDate)} → {fmtDate(p.checkoutDate)}
                    </td>
                    <td>
                      {l.statusText ?? STATUS_FR[l.status]} / {pmsStatus(p)}
                    </td>
                    <td className="pms-num">
                      {l.amountCents === null ? '—' : fmtEuros(l.amountCents)} / {fmtEuros(p.totalCents)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
